import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PlayCircle, StopCircle, Printer, Wallet, Banknote, Receipt, Scale, Clock } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { rp, num, parseNum, fmtDate, rangeFor, todayWIB } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useHardware } from "../context/HardwareContext";
import { PageWrap, StatCard, Empty } from "../components/common";
import { DateFilter } from "../components/DateFilter";
import { ShiftReport } from "../components/ShiftReport";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const today = todayWIB();

const Diff = ({ v }) =>
  v == null ? <span className="text-muted-foreground">-</span> : (
    <span className={`font-mono font-bold ${v === 0 ? "text-success" : v > 0 ? "text-primary" : "text-destructive"}`}>{v > 0 ? "+" : ""}{rp(v)}</span>
  );

const OpenShift = ({ onDone }) => {
  const [cash, setCash] = useState("");
  const [note, setNote] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/shifts/open", { opening_cash: parseNum(cash), note });
      toast.success("Shift dibuka");
      onDone();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border bg-card p-6 space-y-4 max-w-xl" data-testid="open-shift-form">
      <div>
        <h2 className="font-display font-bold text-lg">Buka Shift</h2>
        <p className="text-sm text-muted-foreground">Hitung uang modal di laci sebelum mulai berjualan.</p>
      </div>
      <div className="space-y-1.5"><Label>Kas Awal (modal laci)</Label>
        <Input autoFocus inputMode="numeric" className="h-12 font-mono text-xl font-bold" placeholder="0" value={cash ? num(parseNum(cash)) : ""} onChange={(e) => setCash(e.target.value)} data-testid="opening-cash-input" /></div>
      <div className="space-y-1.5"><Label>Catatan</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Opsional" data-testid="opening-note-input" /></div>
      <Button type="submit" className="h-11" data-testid="open-shift-btn"><PlayCircle className="h-4 w-4 mr-2" />Buka Shift</Button>
    </form>
  );
};

const ActiveShift = ({ shift, onDone }) => {
  const { openDrawer } = useHardware();
  const [cash, setCash] = useState("");
  const [note, setNote] = useState("");
  const counted = parseNum(cash);
  const diff = cash === "" ? null : counted - shift.expected_cash;
  const close = async (e) => {
    e.preventDefault();
    if (cash === "") return toast.error("Masukkan jumlah kas yang dihitung");
    try {
      const { data } = await api.post(`/shifts/${shift.id}/close`, { closing_cash: counted, note });
      toast.success("Shift ditutup");
      onDone(data);
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <div className="space-y-6" data-testid="active-shift">
      <div className="flex flex-wrap items-center gap-3">
        <Badge className="gap-1.5 bg-success text-white hover:bg-success"><span className="h-2 w-2 rounded-full bg-white animate-pulse" />Shift Aktif</Badge>
        <span className="text-sm text-muted-foreground flex items-center gap-1"><Clock className="h-4 w-4" />Dibuka {fmtDate(shift.opened_at)} · {shift.cashier_name}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard testid="shift-opening" icon={Wallet} label="Kas Awal" value={rp(shift.opening_cash)} />
        <StatCard testid="shift-cash-sales" icon={Banknote} tone="success" label="Penjualan Tunai" value={rp(shift.cash_sales)} hint={`Non-tunai ${rp(shift.noncash_sales)}`} />
        <StatCard testid="shift-total-sales" icon={Receipt} tone="chart-5" label="Total Penjualan" value={rp(shift.total_sales)} hint={`${shift.tx_count} transaksi`} />
        <StatCard testid="shift-expected" icon={Scale} tone="warning" label="Kas Seharusnya" value={rp(shift.expected_cash)} hint="kas awal + tunai" />
      </div>
      <form onSubmit={close} className="rounded-2xl border bg-card p-6 grid md:grid-cols-2 gap-6" data-testid="close-shift-form">
        <div className="space-y-4">
          <div>
            <h2 className="font-display font-bold text-lg">Tutup Kasir</h2>
            <p className="text-sm text-muted-foreground">Hitung seluruh uang tunai di laci, lalu masukkan jumlahnya.</p>
          </div>
          <div className="space-y-1.5"><Label>Kas Dihitung</Label>
            <Input inputMode="numeric" className="h-12 font-mono text-xl font-bold" placeholder="0" value={cash ? num(parseNum(cash)) : ""} onChange={(e) => setCash(e.target.value)} data-testid="closing-cash-input" /></div>
          <div className="space-y-1.5"><Label>Catatan</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="cth: selisih karena..." data-testid="closing-note-input" /></div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={openDrawer} data-testid="shift-open-drawer-btn">Buka Laci</Button>
            <Button type="submit" variant="destructive" data-testid="close-shift-btn"><StopCircle className="h-4 w-4 mr-2" />Tutup Shift</Button>
          </div>
        </div>
        <div className="rounded-xl bg-muted/60 p-5 space-y-3 self-start">
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Kas seharusnya</span><span className="font-mono">{rp(shift.expected_cash)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Kas dihitung</span><span className="font-mono">{cash === "" ? "-" : rp(counted)}</span></div>
          <div className="flex justify-between items-center border-t pt-3"><span className="font-semibold">Selisih</span><span className="text-xl" data-testid="closing-diff"><Diff v={diff} /></span></div>
          {diff != null && <p className="text-xs text-muted-foreground">{diff === 0 ? "Kas sesuai." : diff > 0 ? "Kas lebih dari seharusnya." : "Kas kurang dari seharusnya."}</p>}
        </div>
      </form>
    </div>
  );
};

const History = ({ reloadKey, onSelect }) => {
  const { can } = useAuth();
  const isAdmin = can("shifts_all");
  const [preset, setPreset] = useState("7d");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [cashier, setCashier] = useState("all");
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  useEffect(() => { if (isAdmin) api.get("/users").then((r) => setUsers(r.data)); }, [isAdmin]);
  useEffect(() => {
    api.get("/shifts", { params: { ...rangeFor(preset, from, to), cashier_id: cashier === "all" ? "" : cashier } }).then((r) => setRows(r.data));
  }, [preset, from, to, cashier, reloadKey]);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 justify-between items-center">
        <h2 className="font-display font-bold">Riwayat Shift</h2>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto min-w-0">
          <DateFilter {...{ preset, setPreset, from, setFrom, to, setTo }} />
          {isAdmin && (
            <Select value={cashier} onValueChange={setCashier}>
              <SelectTrigger className="w-40" data-testid="shift-cashier-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kasir</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      <div className="rounded-2xl border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kasir</TableHead><TableHead>Buka</TableHead><TableHead className="hidden md:table-cell">Tutup</TableHead>
              <TableHead className="text-right hidden sm:table-cell">Kas Awal</TableHead><TableHead className="text-right">Penjualan</TableHead>
              <TableHead className="text-right hidden lg:table-cell">Kas Seharusnya</TableHead><TableHead className="text-right hidden lg:table-cell">Kas Dihitung</TableHead><TableHead className="text-right">Selisih</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s) => (
              <TableRow key={s.id} className="cursor-pointer" onClick={() => onSelect(s)} data-testid={`shift-row-${s.id}`}>
                <TableCell className="font-semibold">{s.cashier_name}{s.status === "open" && <Badge variant="outline" className="ml-2 text-success">Aktif</Badge>}</TableCell>
                <TableCell className="text-sm">{fmtDate(s.opened_at)}</TableCell>
                <TableCell className="text-sm hidden md:table-cell">{s.closed_at ? fmtDate(s.closed_at) : "-"}</TableCell>
                <TableCell className="text-right font-mono hidden sm:table-cell">{rp(s.opening_cash)}</TableCell>
                <TableCell className="text-right font-mono">{rp(s.total_sales)}</TableCell>
                <TableCell className="text-right font-mono hidden lg:table-cell">{rp(s.expected_cash)}</TableCell>
                <TableCell className="text-right font-mono hidden lg:table-cell">{s.closing_cash != null ? rp(s.closing_cash) : "-"}</TableCell>
                <TableCell className="text-right"><Diff v={s.difference} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {rows.length === 0 && <Empty icon={Clock} text="Belum ada shift pada periode ini." />}
      </div>
    </div>
  );
};

export default function Shift() {
  const { store } = useAuth();
  const { printShift, width } = useHardware();
  const [current, setCurrent] = useState(undefined);
  const [sel, setSel] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const load = useCallback(() => {
    api.get("/shifts/current").then((r) => setCurrent(r.data));
    setReloadKey((k) => k + 1);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <PageWrap>
      {current === undefined ? null : current ? (
        <ActiveShift shift={current} onDone={(s) => { load(); setSel(s); printShift(s); }} />
      ) : (
        <OpenShift onDone={load} />
      )}
      <History reloadKey={reloadKey} onSelect={setSel} />
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-md max-h-[95vh] overflow-y-auto" data-testid="shift-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Laporan Shift</DialogTitle>
            <DialogDescription>{sel?.cashier_name} · {sel && fmtDate(sel.opened_at)}</DialogDescription>
          </DialogHeader>
          {sel && (
            <>
              <div className="bg-muted/60 rounded-xl py-5 overflow-x-auto">
                <div className="mx-auto w-fit drop-shadow-xl"><ShiftReport shift={sel} store={store} width={width} /></div>
              </div>
              <Button onClick={() => printShift(sel)} data-testid="print-shift-btn"><Printer className="h-4 w-4 mr-2" />Cetak Laporan</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageWrap>
  );
}
